import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Building2, ShieldCheck, CheckCircle, 
  DollarSign, ArrowLeftRight, Lock, 
  Wallet, FileCheck
} from 'lucide-react';

const PaymentReconciliationSlide = () => {
  const [reconciledCount, setReconciledCount] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
        setReconciledCount(prev => prev < 100 ? prev + 1 : 0);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="h-full w-full bg-slate-900 flex items-center justify-center p-12 relative overflow-hidden text-white">
        {/* Abstract Background */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-emerald-600/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />

        {/* Floating Agent Header */}
        <div className="absolute top-8 left-8 lg:left-12 z-50 flex items-center gap-4 bg-white/10 backdrop-blur-md p-2 pr-6 rounded-full border border-white/10 shadow-lg">
            <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-slate-700 shadow-sm">
                 <img src="/assets/alisha.png" alt="Alisha" className="w-full h-full object-cover" />
            </div>
            <div className="text-left">
                <h3 className="text-lg font-bold text-white leading-none">Alisha</h3>
                <p className="text-xs text-emerald-400 font-bold uppercase tracking-wide">EOB Posting Agent</p>
            </div>
        </div>

        <div className="flex flex-row h-full w-full max-w-[1600px] gap-12 relative z-10 pt-0">
            {/* Left Side: Context */}
            <div className="w-1/3 flex flex-col justify-center space-y-6">
                <h2 className="text-4xl lg:text-5xl font-bold leading-tight">
                    Secure bank connections for <span className="text-emerald-400">automatic reconciliation</span>.
                </h2>
                
                <p className="text-lg text-slate-300 leading-relaxed">
                   Alisha connects securely to your bank account to verify that payments have actually been deposited. We match every dollar posted in Asha PMS to a real bank transaction, ensuring 100% financial accuracy.
                </p>

                <div className="flex items-center gap-4 p-4 bg-emerald-900/20 border border-emerald-500/30 rounded-xl mt-4">
                    <ShieldCheck className="w-8 h-8 text-emerald-400 flex-shrink-0" />
                    <div>
                        <h4 className="font-bold text-emerald-300">Bank-Grade Security</h4>
                        <p className="text-xs text-emerald-200/70">Encrypted read-only access via Plaid.</p>
                    </div>
                </div>
            </div>

            {/* Right Side: Reconciliation Visualization */}
            <div className="w-2/3 h-full flex items-center justify-center">
                <div className="w-full h-[550px] relative bg-slate-950/50 rounded-3xl border border-slate-800 overflow-hidden flex flex-col">
                    
                    {/* Header */}
                    <div className="h-16 border-b border-slate-800 bg-slate-900 flex items-center justify-between px-8">
                        <div className="flex items-center gap-3">
                            <Building2 className="w-5 h-5 text-slate-400" />
                            <span className="font-bold text-slate-200">Payment Verification Center</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs font-mono text-emerald-400 bg-emerald-500/10 px-3 py-1 rounded-full border border-emerald-500/20">
                            <Lock className="w-3 h-3" /> Secure Connection Active
                        </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 flex relative">
                        {/* Background Lines */}
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-px h-full bg-slate-800/50" />
                        </div>

                        {/* Left: Bank Feed */}
                        <div className="flex-1 p-8 flex flex-col gap-4">
                            <h4 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                                <Wallet className="w-4 h-4" /> Bank Deposits
                            </h4>
                            <div className="space-y-3">
                                {[1,2,3].map((i) => (
                                    <motion.div 
                                        key={i}
                                        initial={{ x: -20, opacity: 0 }}
                                        animate={{ x: 0, opacity: 1 }}
                                        transition={{ delay: i * 0.2 }}
                                        className="bg-slate-900 border border-slate-800 p-4 rounded-xl flex justify-between items-center group hover:border-blue-500/30 transition-colors"
                                    >
                                        <div>
                                            <div className="text-sm font-bold text-slate-200">ACH Deposit - Delta</div>
                                            <div className="text-xs text-slate-500">Tx ID: #8829-{i}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-sm font-bold text-white">$1,250.00</div>
                                            <div className="text-xs text-slate-500">Oct 12</div>
                                        </div>
                                        
                                        {/* Connection Line Node */}
                                        <div className="absolute right-0 top-1/2 w-2 h-2 bg-slate-700 rounded-full translate-x-1/2" />
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        {/* Center: Match Animation */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                             <div className="w-16 h-16 bg-slate-900 rounded-full border-4 border-slate-800 flex items-center justify-center z-10 shadow-xl">
                                <ArrowLeftRight className="w-6 h-6 text-emerald-400" />
                             </div>
                        </div>

                        {/* Right: PMS Payments */}
                        <div className="flex-1 p-8 flex flex-col gap-4">
                             <h4 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2 justify-end">
                                PMS Transactions <FileCheck className="w-4 h-4" /> 
                            </h4>
                             <div className="space-y-3">
                                {[1,2,3].map((i) => (
                                    <motion.div 
                                        key={i}
                                        initial={{ x: 20, opacity: 0 }}
                                        animate={{ x: 0, opacity: 1 }}
                                        transition={{ delay: i * 0.2 + 0.5 }}
                                        className="bg-slate-900 border border-emerald-500/30 p-4 rounded-xl flex justify-between items-center relative"
                                    >
                                        {/* Connection Line Node */}
                                        <div className="absolute left-0 top-1/2 w-2 h-2 bg-emerald-500 rounded-full -translate-x-1/2 shadow-[0_0_10px_rgba(16,185,129,0.5)]" />

                                        <div>
                                            <div className="text-sm font-bold text-slate-200">Payment Posted</div>
                                            <div className="text-xs text-slate-500">Batch #{2024}-{i}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-sm font-bold text-white">$1,250.00</div>
                                            <div className="flex items-center justify-end gap-1 text-xs text-emerald-400 font-bold">
                                                <CheckCircle className="w-3 h-3" /> Verified
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                    </div>
                    
                    {/* Footer Stats */}
                    <div className="h-14 bg-emerald-900/10 border-t border-emerald-900/20 flex items-center justify-center">
                        <span className="text-emerald-400 font-mono text-sm">
                            Matched & Verified transactions this month: <span className="font-bold text-white">428</span>
                        </span>
                    </div>

                </div>
            </div>
        </div>
    </div>
  );
};

export default PaymentReconciliationSlide;
